import { useSelector } from 'react-redux'
import { FormattedMessage } from 'react-intl'

// Components
import { Avatar, If, Text, Button, ExternalLinkButton } from 'src/Components'

// Selectors
import {
  selectCompanyLogo,
  selectCompanyName,
  selectCustomerBrandingStatement,
  selectCustomerEmail,
  selectCustomerFirstName,
  selectCustomerFormattedPhone,
  selectCustomerNMLS,
  selectCustomerPhone,
  selectCustomerPhotoUri,
  selectCustomerWebsite,
  selectFullName,
  selectLinkedinUrl,
  selectOfficeAddress,
  selectOfficeName,
  selectOfficeNmls,
  selectOfficeFormattedPhone,
  selectShowOfficeAddress,
  selectStateLicense,
} from 'src/store/selectors/customerProfile'

// Utils
import { safeExternal } from 'src/helpers/links'

// Assets
import TopoSvg from './TopoSvg'
import QuotesSvg from './QuotesSvg'

// styles
import styles from './CardPro.module.scss'

const CardPro = () => {
  // Company
  const companyLogo = useSelector(selectCompanyLogo)
  const companyName = useSelector(selectCompanyName)

  // Customer Profile
  const customerBrandingStatement = useSelector(selectCustomerBrandingStatement)
  const customerEmail = useSelector(selectCustomerEmail)
  const customerFirstName = useSelector(selectCustomerFirstName)
  const customerFormattedPhone = useSelector(selectCustomerFormattedPhone)
  const customerFullName = useSelector(selectFullName)
  const customerLinkedin = useSelector(selectLinkedinUrl)
  const customerNmls = useSelector(selectCustomerNMLS)
  const customerPhone = useSelector(selectCustomerPhone)
  const customerPhoto = useSelector(selectCustomerPhotoUri)
  const customerStateLicense = useSelector(selectStateLicense)
  const customerWebsite = useSelector(selectCustomerWebsite)

  // Office
  const officeAddress = useSelector(selectOfficeAddress)
  const officeName = useSelector(selectOfficeName)
  const officeNmls = useSelector(selectOfficeNmls)
  const officeFormattedPhone = useSelector(selectOfficeFormattedPhone)
  const showOfficeAddress = useSelector(selectShowOfficeAddress)
  const { street, city, state, zipCode, zip } = officeAddress || {}

  return (
    <div className={styles.root} data-qa='CardPro'>
      <div className={styles.avatarContainer}>
        <Avatar className={styles.avatarShifted} src={customerPhoto} />
        <If condition={companyLogo}>
          <Avatar className={styles.avatarShifted} src={companyLogo} />
        </If>
      </div>

      <div className={styles.container}>
        <div className={styles.nameContainer}>
          <If condition={companyName}>
            <Text
              className={styles.companyName}
              weight='bold'
              variant='neutralCool'
              variantCount='600'
            >
              {companyName}
            </Text>
          </If>

          <If condition={customerFullName}>
            <Text
              className={styles.customerName}
              size='xl'
              weight='bold'
              variant='neutralCool'
              variantCount='900'
            >
              {customerFullName}
            </Text>
          </If>

          <If condition={customerNmls}>
            <Text size='tiny' variant='neutralCool' variantCount='600'>
              <abbr title="Nationwide Multistate Licensing System">NMLS</abbr> #{customerNmls}
            </Text>
          </If>
        </div>

        <If condition={customerBrandingStatement}>
          <div className={styles.noteContainer}>
            <div className={styles.circle}>
              <QuotesSvg />
            </div>
            <div className={styles.noteContent}>
              <Text
                element='div'
                weight='bold'
                size='xs'
                variant='neutralCool'
                variantCount='700'
              >
                <FormattedMessage
                  id='CardPro.customer.note'
                  defaultMessage='A note from {customerFirstName}'
                  values={{
                    customerFirstName,
                  }}
                />
              </Text>
              <Text
                className={styles.note}
                element='p'
                size='s'
                variant='neutralCool'
                variantCount='700'
              >
                {customerBrandingStatement}
              </Text>
            </div>
          </div>
        </If>
        <If condition={officeName}>
          <address>
            <div className={styles.topoContainer}>
              <div className={styles.officeContainer}>
                <div className={styles.officeNameAndNMLS}>
                  <If condition={officeNmls}>
                    <Text size='tiny' variant='neutralCool' variantCount='500'>
                      <abbr title="Nationwide Multistate Licensing System">NMLS</abbr> #{officeNmls}
                    </Text>
                  </If>
                  <Text
                    className={styles.officeName}
                    weight='semibold'
                    size='s'
                    variant='neutralCool'
                    variantCount='900'
                  >
                    {officeName}
                  </Text>
                </div>
                <If condition={showOfficeAddress}>
                  <div className={styles.officeAddress}>
                    <Text size='s' variant='neutralCool' variantCount='800'>
                      {street}
                    </Text>
                    <Text size='xxs' variant='neutralCool' variantCount='600'>
                      {`${city}, ${state} ${zipCode || zip}`}
                    </Text>
                  </div>
                </If>
                <If condition={officeFormattedPhone}>
                  <Button element='a' minimal href={`tel:${officeFormattedPhone}`} className={styles.officePhone}>
                    {officeFormattedPhone}
                  </Button>
                </If>
              </div>
              <TopoSvg className={styles.topoSvg} />
            </div>
          </address>
        </If>

        <dl>
          <address>
            <div className={styles.contactContainer}>
              <div className={styles.rowContainer}>
                <If condition={customerEmail}>
                  <div className={styles.row}>
                    <Text
                      element='dt'
                      weight='semibold'
                      variant='neutralCool'
                      variantCount='800'
                    >
                      <FormattedMessage
                        id='CardPro.customer.email'
                        defaultMessage='Email'
                      />
                    </Text>
                    <Button minimal element='a' href={`mailto:${customerEmail}`}>
                      <dd>{customerEmail}</dd>
                    </Button>
                  </div>
                </If>
                <If condition={customerPhone}>
                  <div className={styles.row}>
                    <Text
                      element='dt'
                      weight='semibold'
                      variant='neutralCool'
                      variantCount='800'
                    >
                      <FormattedMessage
                        id='CardPro.customer.phone'
                        defaultMessage='Phone'
                      />
                    </Text>
                    <Button element='a' minimal href={`tel:${customerPhone}`}>
                      <dd>{customerFormattedPhone}</dd>
                    </Button>
                  </div>
                </If>
                <If condition={customerWebsite}>
                  <div className={styles.row}>
                    <Text
                      element='dt'
                      weight='semibold'
                      variant='neutralCool'
                      variantCount='800'
                    >
                      <FormattedMessage
                        id='CardPro.customer.website'
                        defaultMessage='Website'
                      />
                    </Text>
                    <ExternalLinkButton
                      minimal
                      element='a'
                      href={safeExternal(customerWebsite)}
                    >
                      <dd>{customerWebsite}</dd>
                    </ExternalLinkButton>
                  </div>
                </If>
                <If condition={customerLinkedin}>
                  <div className={styles.row}>
                    <Text
                      element='dt'
                      weight='semibold'
                      variant='neutralCool'
                      variantCount='800'
                    >
                      <FormattedMessage
                        id='CardPro.customer.linkedin'
                        defaultMessage='LinkedIn'
                      />
                    </Text>
                    <ExternalLinkButton
                      minimal
                      element='a'
                      href={safeExternal(customerLinkedin)}
                    >
                      <dd>{customerLinkedin}</dd>
                    </ExternalLinkButton>
                  </div>
                </If>
                <If condition={customerStateLicense}>
                  <div className={styles.row}>
                    <Text
                      element='dt'
                      weight='semibold'
                      variant='neutralCool'
                      variantCount='800'
                    >
                      <FormattedMessage
                        id='CardPro.customer.stateLicense'
                        defaultMessage='State license'
                      />
                    </Text>
                      <dd>{customerStateLicense}</dd>
                  </div>
                </If>
              </div>
            </div>
          </address>
        </dl>
      </div>
    </div>
  )
}

export default CardPro
